import { FormControl, StandardTextFieldProps } from '@material-ui/core';
import { Locale } from '@nemuru-eng/js-api-client';
import React from 'react';
import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

import { CustomInputDate } from '@/components/shared/components/CustomInputDate';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import { CustomFormLabel } from '@/components/shared/components/NewContextForms/shared/CustomFormLabel';
import { CustomFormLabelWithAdornment } from '@/components/shared/components/NewContextForms/shared/CustomFormLabelWithAdornment';
import { CustomTextFieldExtraProps } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useScreen } from '@/hooks/useScreen';

interface InputDateProps {
  autoOk?: boolean;
  allowKeyboardPicking?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  locale?: Locale;
  emptyLabel?: string;
  openTo?: 'year' | 'month' | 'date';
  views?: ('year' | 'month' | 'date')[];
  format?: string;
  minDate?: Date;
  maxDate?: Date;
  markAsOptional?: boolean;
  keepTimezone?: boolean;
}

export interface CustomDateFieldProps<TFieldValues extends FieldValues>
  extends Omit<StandardTextFieldProps, 'error' | 'style' | 'label'>,
    InputDateProps,
    CustomTextFieldExtraProps<TFieldValues> {}

export const CustomDateField = React.forwardRef<HTMLInputElement, CustomDateFieldProps<FieldValues>>(
  (
    {
      field,
      fieldVariant = FieldVariant.dataRow,
      error,
      tooltipTitle,
      labelIcon,
      label,
      labelIconPosition,
      allowKeyboardPicking = true,
      markAsOptional = false,
      highlightLabel,
      ...props
    },
    ref,
  ) => {
    const { isMobile } = useScreen();
    const { getTextIdTranslation } = useTranslation();
    const { getMUIDateClasses, inputDateThemeParams } = useCustomFieldStyles({
      disabled: props.disabled,
      field: !!field,
      fieldVariant,
      highlightLabel,
    });

    const fieldVariantIsForm = fieldVariant === FieldVariant.form;
    const classes = getMUIDateClasses();

    return (
      <>
        <FormControl fullWidth classes={classes}>
          <CustomInputDate
            // @ts-expect-error - ref is not typed correctly for CustomInputDate
            ref={ref}
            themeParams={inputDateThemeParams}
            {...(!field && { defaultValue: props.value })}
            allowKeyboardPicking={!isMobile && allowKeyboardPicking}
            locale={getTextIdTranslation('DATE_LOCALE')}
            placeholder={getTextIdTranslation('DATE_FORMAT_DAY_MONTH_YEAR')}
            field={field as ControllerRenderProps<FieldValues, Path<FieldValues>>}
            fullWidth
            openTo="year"
            autoOk
            views={['year', 'month', 'date']}
            format="dd/MM/yyyy"
            label={
              fieldVariantIsForm &&
              label && (
                <CustomFormLabel
                  label={label}
                  markAsOptional={markAsOptional}
                  color={highlightLabel ? 'textPrimary' : 'textSecondary'}
                />
              )
            }
            InputProps={
              !fieldVariantIsForm && label
                ? {
                    startAdornment: (
                      <CustomFormLabelWithAdornment
                        tooltipTitle={tooltipTitle}
                        labelIcon={labelIcon}
                        label={label}
                        labelIconPosition={labelIconPosition}
                      />
                    ),
                  }
                : {}
            }
            error={error}
            {...props}
            InputLabelProps={{
              shrink: fieldVariantIsForm,
              ...props?.InputLabelProps,
            }}
            iconStyles={{ top: fieldVariantIsForm ? 24 : 12 }}
            {...field}
            {...(field && {
              inputRef: field.ref,
            })}
          />
        </FormControl>
      </>
    );
  },
);
